import { MessageSquareText } from 'lucide-react';

import { Callout } from '@/_shared/components/ui/callout';
import type { SSEMessage } from '@/_shared/types';

import { MessageInput } from '../../chat/components/MessageInput';
import { MessageList } from '../../chat/components/MessageList';
import { useChatFeedback } from '../../chat/hooks/useChatFeedback';
import { useRefreshChatFeedbackOnIdle } from '../../chat/hooks/useRefreshChatFeedbackOnIdle';
import type { PanelMention } from '../../chat/lib/panel-mentions';
import { generateSuggestedPrompts } from '../lib/dashboard-prompts';
import type { Dashboard } from '../types';

interface DashboardChatSidebarProps {
  dashboard: Dashboard;
  paramValues: Record<string, string | string[]>;
  /** Current dashboard-chat session; feedback is scoped to it. */
  sessionId: string | null;
  messages: SSEMessage[];
  agentActive: boolean;
  /** True when a confirmed turn ID is set — gates the Stop button. */
  canStop?: boolean;
  stopping?: boolean;
  stopStatusError?: boolean;
  stopStalled?: boolean;
  onRetryStopStatus?: () => void;
  /** Disable input until session is ready. */
  disabled?: boolean;
  /**
   * Demo workspace only: this thread hit its LLM budget, so the SERVER will refuse the next turn
   * (`dashboard-chat.ts` -> `isBudgetExhausted`). Without this the composer stays enabled here and
   * the refusal only surfaces as a bare FORBIDDEN toast after the user has already typed and sent.
   */
  budgetExhausted?: boolean;
  onSend: (content: string, invokedSkillIds?: string[], referencedPanelIds?: string[]) => void;
  onReset: () => void;
  onClose: () => void;
  onStop?: () => void;
  prefillValue?: string;
  prefillNonce?: number;
  /** Panels "@" can name, labelled so each one names exactly a single panel. */
  panelMentions?: PanelMention[];
  onPanelMentioned?: (mention: PanelMention) => void;
  /** A mention to write into the draft, queued when a panel's ✨ is clicked. */
  pendingMention?: PanelMention;
  pendingMentionNonce?: number;
}

function EmptyState({
  dashboard,
  disabled,
  onSend,
}: {
  dashboard: Dashboard;
  disabled: boolean;
  onSend: (content: string) => void;
}): React.ReactElement {
  const prompts = generateSuggestedPrompts(dashboard);
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
        <MessageSquareText size={20} />
      </div>
      <p className="text-sm font-medium text-foreground">Ask about this dashboard</p>
      <p className="mt-1 text-center text-xs text-muted-foreground">Analyze data, explain trends, and find anomalies</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {prompts.map((p, i) => (
          <button
            key={i}
            onClick={() => onSend(p.prompt)}
            disabled={disabled}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DashboardChatSidebar({
  dashboard,
  sessionId,
  messages,
  agentActive,
  canStop = false,
  stopping = false,
  stopStatusError = false,
  stopStalled = false,
  onRetryStopStatus,
  disabled = false,
  budgetExhausted = false,
  onSend,
  onStop,
  prefillValue,
  prefillNonce,
  panelMentions,
  onPanelMentioned,
  pendingMention,
  pendingMentionNonce,
}: DashboardChatSidebarProps): React.ReactElement {
  const hasMessages = messages.length > 0;
  const feedback = useChatFeedback(sessionId);
  useRefreshChatFeedbackOnIdle(sessionId, agentActive, feedback.refresh);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {hasMessages ? (
        <MessageList
          messages={messages}
          suggestionSurface="dashboard"
          thinking={agentActive}
          onAction={onSend}
          compact
          actionsDisabled={agentActive}
          feedback={{
            canRate: feedback.canRate,
            eligibleTurnIds: feedback.eligibleTurnIds,
            ratingByTurn: feedback.ratingByTurn,
            isPending: feedback.isPending,
            onRate: feedback.setRating,
          }}
        />
      ) : (
        <EmptyState dashboard={dashboard} disabled={disabled} onSend={onSend} />
      )}

      {budgetExhausted && (
        <div className="px-3 pt-3">
          <Callout variant="info">The budget for this thread is over. Start a new chat to continue exploring.</Callout>
        </div>
      )}
      <MessageInput
        onSend={onSend}
        disabled={agentActive || disabled || budgetExhausted}
        // A running turn blocks Send and Enter, and leaves the field open so the next message can
        // be drafted. A session that is not ready, or a thread that is over budget, still locks it.
        locked={disabled || budgetExhausted}
        agentLoaded={canStop}
        stopping={stopping}
        stopStatusError={stopStatusError}
        stopStalled={stopStalled}
        onRetryStopStatus={onRetryStopStatus}
        variant="sidebar"
        // Override the generic disabled placeholder — this thread isn't busy, it's over, and the
        // Callout above already says why. Same wording as ChatConversation.
        {...(budgetExhausted ? { placeholder: 'This thread is closed' } : {})}
        onStop={canStop ? onStop : undefined}
        prefillValue={prefillValue}
        prefillNonce={prefillNonce}
        panelOptions={panelMentions}
        onPanelMentioned={onPanelMentioned}
        pendingMention={pendingMention}
        pendingMentionNonce={pendingMentionNonce}
      />
    </div>
  );
}
